import React from "react";
import { motion } from "framer-motion";
import { Share2, Clock, MapPin, TrainFront, Phone } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { CONTACT, REAL, PRICE } from "@/data/site";
import { Reveal, Kicker } from "./Reveal";
import { Laurel } from "./Crest";
import { openWhatsApp } from "@/lib/wa";

const POSTER_DISHES = [
  { name: "Thiep", img: REAL.tieb },
  { name: "Yassa", img: REAL.yassa },
  { name: "Maffé", img: REAL.maffe },
  { name: "Attiéké", img: REAL.poisson },
  { name: "Brochette", img: REAL.brochette },
];

const HalalSeal = () => (
  <div className="flex h-14 w-14 flex-col items-center justify-center rounded-full border border-[var(--gold)]/70 text-[var(--gold)] shadow-[inset_0_0_12px_rgba(212,175,55,0.15)] md:h-16 md:w-16">
    <span className="font-serif text-base leading-none md:text-lg">حلال</span>
    <span className="mt-0.5 font-sans text-[8px] uppercase tracking-[0.24em]">Halal</span>
  </div>
);

const Ornament = () => (
  <svg viewBox="0 0 120 20" className="h-4 w-28 text-[var(--gold)]/70" fill="none" stroke="currentColor" strokeWidth="1" aria-hidden="true">
    <path d="M2 10h40M78 10h40" strokeLinecap="round" />
    <path d="M52 10c3-5 6-5 8 0-2 5-5 5-8 0zM68 10c-3-5-6-5-8 0 2 5 5 5 8 0z" />
    <circle cx="60" cy="10" r="1.6" fill="currentColor" stroke="none" />
  </svg>
);

export default function Flyer() {
  const { t } = useLang();

  return (
    <section id="flyer" className="relative overflow-hidden py-24 md:py-36" data-testid="flyer-section">
      <div className="pointer-events-none absolute inset-0 opacity-40 [background:radial-gradient(600px_400px_at_20%_30%,rgba(139,0,0,0.18),transparent),radial-gradient(500px_400px_at_85%_70%,rgba(212,175,55,0.1),transparent)]" />

      <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-5 md:px-10 lg:grid-cols-2">
        {/* Left: copy */}
        <div>
          <Kicker className="mb-5">{t.flyerKicker}</Kicker>
          <h2 className="font-serif text-5xl leading-[0.95] tracking-tight text-[var(--text)] md:text-7xl">
            {t.flyerTitle} <span className="italic gold-gradient-text">{t.flyerTitleEm}</span>
          </h2>
          <Reveal delay={0.1}>
            <p className="mt-6 max-w-md font-sans text-[15px] leading-relaxed text-[var(--text-2)]">
              {t.flyerIntro}
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <button
                onClick={() => openWhatsApp(t.waOrder)}
                data-testid="flyer-order-btn"
                className="bg-[var(--gold)] px-8 py-4 font-sans text-sm font-semibold tracking-wide text-[var(--bg)] transition-transform hover:scale-[1.02]"
              >
                {t.waBtn}
              </button>
              <button
                onClick={() => openWhatsApp(`${t.flyerShare} — Le Goût du Fouta`)}
                data-testid="flyer-share-btn"
                className="inline-flex items-center gap-2 border border-[var(--gold)]/50 px-8 py-4 font-sans text-sm tracking-wide text-[var(--gold)] transition-colors hover:bg-[var(--gold)]/10"
              >
                <Share2 size={16} /> {t.flyerShare}
              </button>
            </div>
          </Reveal>
        </div>

        {/* Right: the poster */}
        <motion.div
          initial={{ opacity: 0, y: 50, rotateX: 8 }}
          whileInView={{ opacity: 1, y: 0, rotateX: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          className="mx-auto w-full max-w-[440px]"
          style={{ perspective: 1000 }}
          data-testid="flyer-poster"
        >
          <div className="relative overflow-hidden rounded-[3px] bg-[#080808] p-[6px] shadow-2xl shadow-black/60 ring-1 ring-[var(--gold)]/30">
            <div className="relative flex flex-col items-center border border-[var(--gold)]/25 px-6 py-8 text-center sm:px-8">
              {/* corner flourishes */}
              <span className="pointer-events-none absolute left-2 top-2 h-6 w-6 border-l border-t border-[var(--gold)]/40" />
              <span className="pointer-events-none absolute right-2 top-2 h-6 w-6 border-r border-t border-[var(--gold)]/40" />
              <span className="pointer-events-none absolute bottom-2 left-2 h-6 w-6 border-b border-l border-[var(--gold)]/40" />
              <span className="pointer-events-none absolute bottom-2 right-2 h-6 w-6 border-b border-r border-[var(--gold)]/40" />

              {/* top row */}
              <div className="flex w-full items-center justify-between">
                <span className="max-w-[90px] text-left font-sans text-[9px] uppercase leading-tight tracking-[0.22em] text-[var(--text-2)]">
                  {t.flyerRestaurant}
                </span>
                <HalalSeal />
              </div>

              {/* Title + laurels */}
              <div className="relative mt-4 flex items-center justify-center gap-1">
                <Laurel className="h-24 w-auto text-[var(--gold)]/80" />
                <h3 className="font-serif text-4xl leading-[0.86] tracking-tight sm:text-5xl">
                  <span className="block gold-gradient-text">LE GOÛT</span>
                  <span className="block text-[var(--gold)]">DU</span>
                  <span className="block gold-gradient-text">FOUTA</span>
                </h3>
                <Laurel className="h-24 w-auto text-[var(--gold)]/80" flip />
              </div>

              <div className="mt-5">
                <Ornament />
              </div>

              {/* Hours */}
              <div className="mt-4 flex items-center justify-center gap-2 text-[var(--gold)]">
                <Clock size={15} />
                <span className="font-serif text-2xl italic">{t.open}</span>
              </div>
              <p className="font-sans text-sm tracking-wide text-[var(--text)]">{t.hours}</p>

              {/* Catering */}
              <p className="mt-6 font-serif text-lg italic text-[var(--gold)]">{t.flyerCommande}</p>
              <p className="mt-2 max-w-[300px] font-sans text-[11px] uppercase leading-relaxed tracking-[0.14em] text-[var(--text)]">
                {t.events.join(" · ")}
              </p>

              {/* Dishes */}
              <div className="mt-7 grid w-full grid-cols-5 gap-2">
                {POSTER_DISHES.map((d) => (
                  <div key={d.name} className="flex flex-col items-center">
                    <div className="aspect-square w-full overflow-hidden rounded-full border border-[var(--gold)]/50 p-[2px]">
                      <img src={d.img} alt={d.name} loading="lazy" className="h-full w-full rounded-full object-cover" />
                    </div>
                    <span className="mt-1.5 font-serif text-[11px] italic text-[var(--gold-light)] sm:text-xs">{d.name}</span>
                  </div>
                ))}
              </div>

              {/* Price */}
              <p className="mt-6 max-w-[300px] font-serif text-lg font-semibold leading-snug tracking-wide text-[#E0563B]">
                {t.flyerPrice}
              </p>

              <div className="my-5 h-px w-full bg-gradient-to-r from-transparent via-[var(--gold)]/50 to-transparent" />

              {/* Footer info */}
              <p className="font-serif text-lg font-semibold uppercase tracking-wide text-[var(--text)] sm:text-xl">
                {t.flyerOnSite}
              </p>
              <div className="mt-3 space-y-1.5 font-sans text-[12px] text-[var(--text-2)]">
                <p className="flex items-center justify-center gap-2">
                  <MapPin size={13} className="text-[var(--gold)]" /> {CONTACT.address}, {CONTACT.city}
                </p>
                <p className="flex items-center justify-center gap-2">
                  <TrainFront size={13} className="text-[var(--gold)]" /> {CONTACT.metro}
                </p>
                <a
                  href={`tel:${CONTACT.phoneTel}`}
                  className="flex items-center justify-center gap-2 text-[var(--gold)] transition-colors hover:text-[var(--gold-light)]"
                  data-testid="flyer-phone"
                >
                  <Phone size={13} /> TÉL : {CONTACT.phoneDisplay}
                </a>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
